// BlazePose3DViewer/src/App.js
import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import '../App.css';

const SKELETON_CONNECTIONS = [
  [11, 13], [13, 15],
  [12, 14], [14, 16],
  [23, 25], [25, 27],
  [24, 26], [26, 28],
  [11, 12], [23, 24],
  [11, 23], [12, 24]
];

function ViewPutt3D() {
  const mountRef = useRef(null);
  const [data, setData] = useState(null);
  const [frameIndex, setFrameIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    fetch('/blaze_pose_1217.json')
      .then(res => res.json())
      .then(json => setData(json.frames || []));
  }, []);

  useEffect(() => {
    if (!data) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(0, 0, 3);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    mountRef.current?.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, 0);
    controls.update();

    scene.add(new THREE.AxesHelper(0.5));
    scene.add(new THREE.GridHelper(2, 10));

    const light = new THREE.DirectionalLight(0xffffff, 1);
    light.position.set(0, 0, 2);
    scene.add(light);

    const spheres = [];
    for (let i = 0; i < 33; i++) {
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(0.015, 8, 8),
        new THREE.MeshStandardMaterial({ color: 0xff0000 })
      );
      scene.add(sphere);
      spheres.push(sphere);
    }

    const lines = SKELETON_CONNECTIONS.map(([start, end]) => {
      const geometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(), new THREE.Vector3()
      ]);
      const line = new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: 0x00ff00 }));
      scene.add(line);
      return { line, start, end };
    });

    const scale = 2.5;
    let rafId;

    const animate = () => {
      rafId = requestAnimationFrame(animate);
      if (!data[frameIndex]) return;

      const frame = data[frameIndex];
      const hipCenter = {
        x: (frame[23].x + frame[24].x) / 2,
        y: (frame[23].y + frame[24].y) / 2,
        z: (frame[23].z + frame[24].z) / 2,
      };

      for (let i = 0; i < 33; i++) {
        const { x, y, z } = frame[i];
        const px = (x - hipCenter.x) * scale;
        const py = (y - hipCenter.y) * scale;
        const pz = (z - hipCenter.z) * scale;
        spheres[i].position.set(px, -py, pz);
      }

      lines.forEach(({ line, start, end }) => {
        const startVec = spheres[start].position;
        const endVec = spheres[end].position;
        line.geometry.setFromPoints([startVec, endVec]);
      });

      controls.update();
      renderer.render(scene, camera);
    };

    animate();

    const interval = setInterval(() => {
      if (isPlaying) {
        setFrameIndex(prev => (prev + 1) % data.length);
      }
    }, 100);

    return () => {
      clearInterval(interval);
      cancelAnimationFrame(rafId);
      if (mountRef.current && renderer.domElement.parentNode === mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [data, frameIndex, isPlaying]);

  return (
    <div className="App">
      <div ref={mountRef} className="viewer" />
      {data && (
        <div className="controls">
          <button onClick={() => setIsPlaying(!isPlaying)}>
            {isPlaying ? 'Pause' : 'Play'}
          </button>
          <input
            type="range"
            min="0"
            max={data.length - 1}
            value={frameIndex}
            onChange={(e) => setFrameIndex(parseInt(e.target.value))}
          />
          <span>Frame {frameIndex + 1} / {data.length}</span>
        </div>
      )}
    </div>
  );
}

export default ViewPutt3D;
